import React, { useState, useRef, useCallback, useEffect } from 'react';
import { BTN } from '../constants/buttons';
import { buildGradingFormDoc } from '../utils/gradingPdf';

// Exports the user's grading record as the official 6-page JIGS SFX grading
// application form, as a PDF with REAL, SELECTABLE TEXT.
//
// Why pdfmake (not html2canvas / SharePdfButton)?
//   SharePdfButton rasterises the DOM with html2canvas and embeds a bitmap —
//   the resulting PDF text cannot be selected, copied or searched. pdfmake
//   draws each glyph from an embedded TrueType font, so the output is true
//   vector text. We therefore use pdfmake directly here.
//
// pdfmake (+ its font VFS) is large, so we lazy-load both only on click.

// Centred-fit image embed on a fresh A4 page, with a small grey "Attachment:
// <label>" header at the top of the page. Used for every uploaded image.
function appendImagePage(doc, img, label, font, rgb) {
  const page = doc.addPage([595.28, 841.89]); // A4 portrait, points
  const margin = 50;
  const headerSpace = 30;
  const maxW = page.getWidth() - margin * 2;
  const maxH = page.getHeight() - margin * 2 - headerSpace;
  const scale = Math.min(maxW / img.width, maxH / img.height, 1);
  const w = img.width * scale;
  const h = img.height * scale;
  page.drawImage(img, {
    x: (page.getWidth() - w) / 2,
    y: (page.getHeight() - h - headerSpace) / 2,
    width: w,
    height: h,
  });
  page.drawText(`Attachment: ${label}`, {
    x: margin,
    y: page.getHeight() - 30,
    size: 9,
    font,
    color: rgb(0.5, 0.5, 0.5),
  });
}

// Draw the passport photo into the top-right of page 1, then append each
// supporting-document attachment. Images become one fit-to-page A4 page
// each; PDFs preserve their original page count.
//
// The photo is drawn here (via pdf-lib) rather than embedded in the doc-def
// (via pdfmake) because pdfmake's image embedder hangs silently on some
// JPEG variants — pdf-lib's embedJpg/embedPng is robust by comparison.
async function mergePhotoAndAttachments(baseBuffer, attachments, photoBytes, photoMime) {
  const docs = (attachments || []).filter((a) => a.kind !== 'photo');
  if (docs.length === 0 && !photoBytes) {
    return new Blob([baseBuffer], { type: 'application/pdf' });
  }
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const doc = await PDFDocument.load(baseBuffer);
  const font = await doc.embedFont(StandardFonts.Helvetica);

  // Photo overlay on page 1 (top-right area, just under the section heading).
  if (photoBytes && doc.getPageCount() > 0) {
    try {
      let img;
      if (photoMime === 'image/png') {
        img = await doc.embedPng(photoBytes);
      } else {
        // Default to JPEG — covers image/jpeg and anything else the user
        // somehow got past the server's JPEG/PNG whitelist.
        img = await doc.embedJpg(photoBytes);
      }
      const page = doc.getPage(0);
      const targetW = 130;
      const targetH = 163;
      // Letterbox to fit within targetW x targetH while preserving aspect.
      const scale = Math.min(targetW / img.width, targetH / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      // Drop the photo down so its top edge sits flush with the BOTTOM of
      // the Section 1 orange heading bar — i.e. starts at the same y as
      // the labelValueRows on the left.
      //   page top margin       = 40
      //   sectionHeading top margin = 10  (gradingPdf.js line 141)
      //   heading bar height    ≈ 18 (fontSize 11 + 3pt + 3pt + cell padding)
      //   sectionHeading bottom margin = 4
      //   → labelValueRows starts ~72pt from the page top.
      // pdf-lib uses bottom-left origin, so we subtract from pageHeight.
      const sideMargin = 40;
      // Tuned to sit a few mm below the Section 1 heading bar.
      const detailsTopFromPageTop = 80;
      const x = page.getWidth() - sideMargin - w;
      const y = page.getHeight() - detailsTopFromPageTop - h;
      page.drawImage(img, { x, y, width: w, height: h });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[GradingExport] photo embed via pdf-lib failed', err);
    }
  }

  for (const att of docs) {
    try {
      const res = await fetch(att.download_url, { credentials: 'include' });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error('[GradingExport] attachment fetch failed', att.file_name, res.status);
        continue;
      }
      const bytes = await res.arrayBuffer();
      const label = att.label || att.file_name;
      if (att.file_type === 'application/pdf') {
        const src = await PDFDocument.load(bytes);
        const pages = await doc.copyPages(src, src.getPageIndices());
        pages.forEach((p, i) => {
          doc.addPage(p);
          if (i === 0) {
            p.drawText(`Attachment: ${label}`, {
              x: 40, y: p.getHeight() - 30,
              size: 9, font, color: rgb(0.5, 0.5, 0.5),
            });
          }
        });
      } else if (att.file_type === 'image/png') {
        const img = await doc.embedPng(bytes);
        appendImagePage(doc, img, label, font, rgb);
      } else if (/^image\/jpe?g$/i.test(att.file_type)) {
        const img = await doc.embedJpg(bytes);
        appendImagePage(doc, img, label, font, rgb);
      } else {
        // eslint-disable-next-line no-console
        console.warn('[GradingExport] unsupported attachment type', att.file_type, att.file_name);
      }
    } catch (err) {
      // One bad attachment shouldn't sink the whole export.
      // eslint-disable-next-line no-console
      console.error('[GradingExport] failed to append', att.file_name, err);
    }
  }
  const out = await doc.save();
  return new Blob([out], { type: 'application/pdf' });
}

function GradingExportButton({ profile, entries, progress, attachments, disabled }) {
  const [generating, setGenerating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState('');
  const [canShare, setCanShare] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  // Synchronous in-flight flag — the `generating` state is async and would be
  // stale across a same-tick double-click, so we guard on a ref instead.
  const generatingRef = useRef(false);

  // Feature-detect Web Share with files (mobile mostly). Done in an effect so
  // SSR / non-browser test envs never touch navigator.
  useEffect(() => {
    try {
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [new File([''], 'test.pdf', { type: 'application/pdf' })] })
      ) {
        setCanShare(true);
      }
    } catch {
      setCanShare(false);
    }
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const filename = useCallback(() => {
    const name = (profile && profile.full_name ? String(profile.full_name) : 'application').trim() || 'application';
    return `JIGS-Grading-${name.replace(/\s+/g, '-')}.pdf`;
  }, [profile]);

  // Lazy-load pdfmake + its font VFS, generate the JIGS form PDF, then —
  // if the user has any supporting documents — merge them in via pdf-lib
  // (also lazy-loaded). Returns a Blob ready to download/print/share.
  //
  // Why pdfmake + pdf-lib? pdfmake gives us a high-level doc-def → vector
  // PDF (perfect for the form layout) but cannot embed external PDFs or
  // arbitrary images outside its layout engine. pdf-lib excels at low-level
  // page manipulation (copyPages, drawImage). Together: clean form + native
  // attachment appending without rasterising anything.
  // True when the export needs pdf-lib's merge step (photo overlay or
  // appended documents). When false, we can use pdfmake's `download`/
  // `print` APIs directly — which avoids `getBuffer`/`getBlob` entirely
  // (those have hung intermittently in pdfmake 0.3.9).
  const needsMerge = useCallback(() => {
    const list = attachments || [];
    if (list.some((a) => a.kind === 'photo')) return true;
    return list.some((a) => a.kind !== 'photo');
  }, [attachments]);

  // Build the pdfmake doc + lazy-load the photo bytes. Returns the raw pdf
  // object plus the photo bytes/mime so handlers can decide which output
  // path to take. Does NOT call getBuffer/getBlob — that's per-handler.
  const buildPdf = useCallback(async () => {
    const [pdfMakeMod, pdfFontsMod] = await Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts'),
    ]);
    const pdfMake = pdfMakeMod.default || pdfMakeMod;
    const pdfFonts = pdfFontsMod.default || pdfFontsMod;
    // Resolve the font virtual-file-system across the various export shapes:
    //   0.1.x: pdfFonts.pdfMake.vfs
    //   some bundles: pdfFonts.vfs
    //   0.3.x CJS: pdfFonts itself IS the vfs (default export = module.exports)
    const vfs =
      (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
      (pdfFonts && pdfFonts.vfs) ||
      pdfFonts ||
      pdfMake.vfs;
    if (vfs) {
      if (typeof pdfMake.addVirtualFileSystem === 'function') {
        pdfMake.addVirtualFileSystem(vfs);
      } else {
        pdfMake.vfs = vfs;
      }
    }

    // Pre-fetch the passport photo bytes (if any). The photo is NOT embedded
    // via pdfmake (its image embedder hangs on some bitmaps); pdf-lib draws
    // the photo onto page 1 in the merge step. Robust by comparison.
    let photoBytes = null;
    let photoMime = null;
    const photo = (attachments || []).find((a) => a.kind === 'photo');
    if (photo) {
      try {
        const res = await fetch(photo.download_url, { credentials: 'include' });
        if (res.ok) {
          photoBytes = await res.arrayBuffer();
          photoMime = photo.file_type;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[GradingExport] photo fetch failed — skipping photo overlay', e);
      }
    }

    const generatedDate = new Date().toLocaleDateString('en-GB');
    const doc = buildGradingFormDoc(profile, entries, progress, generatedDate, !!photoBytes);
    const pdf = pdfMake.createPdf(doc);
    return { pdf, photoBytes, photoMime };
  }, [profile, entries, progress, attachments]);

  // Convert a pdfmake `pdf` to a Blob via getBlob. Race with a 30s timeout
  // so any future stall surfaces as an actionable error instead of an
  // infinite spinner. Only called when we actually need bytes (for the
  // pdf-lib merge that draws the photo + appends documents).
  const pdfToBlob = useCallback((pdf) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('PDF render timed out')), 30000);
    try {
      pdf.getBlob((blob) => {
        clearTimeout(timer);
        resolve(blob);
      });
    } catch (err) {
      clearTimeout(timer);
      reject(err);
    }
  }), []);

  const onTrigger = useCallback(async () => {
    if (generating) return;
    setError('');
    // Open the action menu immediately (no pre-generation needed — each action
    // builds the pdf on demand, which is fast for a text-only document).
    setMenuOpen((open) => !open);
  }, [generating]);

  // Run an action with the generating-state guard. The action receives the
  // built `{ pdf, photoBytes, photoMime }` bundle and decides which output
  // path to take (direct pdfmake API for no-merge cases, blob+pdf-lib merge
  // otherwise).
  const withGenerating = useCallback(async (fn) => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    setError('');
    try {
      const bundle = await buildPdf();
      await fn(bundle);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[GradingExport] failed:', err);
      setError('Could not generate the PDF. Please try again.');
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  }, [buildPdf]);

  const handleDownload = useCallback(() => {
    withGenerating(async ({ pdf, photoBytes, photoMime }) => {
      const name = filename();
      if (!needsMerge()) {
        // Fast path: pdfmake's own download. No getBuffer/getBlob involved.
        pdf.download(name);
      } else {
        // Merge path: bytes via getBlob → pdf-lib draws photo + appends docs.
        const baseBlob = await pdfToBlob(pdf);
        const baseBuffer = await baseBlob.arrayBuffer();
        const mergedBlob = await mergePhotoAndAttachments(baseBuffer, attachments, photoBytes, photoMime);
        const url = URL.createObjectURL(mergedBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      setMenuOpen(false);
    });
  }, [withGenerating, filename, needsMerge, attachments, pdfToBlob]);

  const handlePrint = useCallback(() => {
    withGenerating(async ({ pdf, photoBytes, photoMime }) => {
      if (!needsMerge()) {
        // Fast path: pdfmake's own print dialog.
        pdf.print();
      } else {
        // Merge path: open blob URL in a new window and trigger print.
        const baseBlob = await pdfToBlob(pdf);
        const baseBuffer = await baseBlob.arrayBuffer();
        const mergedBlob = await mergePhotoAndAttachments(baseBuffer, attachments, photoBytes, photoMime);
        const url = URL.createObjectURL(mergedBlob);
        const win = window.open(url, '_blank');
        if (win) {
          const tryPrint = () => { try { win.focus(); win.print(); } catch { /* user closed */ } };
          win.addEventListener('load', tryPrint, { once: true });
          setTimeout(tryPrint, 500);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
      setMenuOpen(false);
    });
  }, [withGenerating, needsMerge, attachments, pdfToBlob]);

  const handleShare = useCallback(() => {
    withGenerating(async ({ pdf, photoBytes, photoMime }) => {
      const name = filename();
      let shareBlob;
      if (!needsMerge()) {
        shareBlob = await pdfToBlob(pdf);
      } else {
        const baseBlob = await pdfToBlob(pdf);
        const baseBuffer = await baseBlob.arrayBuffer();
        shareBlob = await mergePhotoAndAttachments(baseBuffer, attachments, photoBytes, photoMime);
      }
      try {
        const file = new File([shareBlob], name, { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'JIGS SFX Grading Application' });
        }
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('[GradingExport] share failed:', err);
        }
      }
      setMenuOpen(false);
    });
  }, [withGenerating, filename, needsMerge, attachments, pdfToBlob]);

  const menuItemClass =
    'w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 ' +
    'hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50';

  return (
    <div className="relative w-full md:w-auto">
      <button
        ref={buttonRef}
        type="button"
        onClick={onTrigger}
        disabled={disabled || generating}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={`${BTN.primary} w-full md:w-auto justify-center`}
        title="Export your grading record as the official JIGS application form (PDF)"
        data-testid="grading-export-button"
      >
        {generating ? (
          <>
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating…
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export application (PDF)
          </>
        )}
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute top-full mt-1 right-0 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 min-w-[200px] py-1"
          data-testid="grading-export-menu"
        >
          <button type="button" onClick={handleDownload} disabled={generating} className={menuItemClass} data-testid="grading-export-download">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download PDF
          </button>
          <button type="button" onClick={handlePrint} disabled={generating} className={menuItemClass} data-testid="grading-export-print">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print…
          </button>
          {canShare && (
            <button type="button" onClick={handleShare} disabled={generating} className={menuItemClass} data-testid="grading-export-share">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share…
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="absolute top-full mt-1 right-0 text-xs text-red-600 dark:text-red-400 whitespace-nowrap" data-testid="grading-export-error">
          {error}
        </p>
      )}
    </div>
  );
}

export default GradingExportButton;
